import { Avatar, Card, Chip, EmptyState, Link, Skeleton } from "@heroui/react";
import { useParams } from "react-router";

import { AccountBadge } from "@/src/components/AccountBadge";
import { DetailSection } from "@/src/components/DetailSection";
import { useSetBreadcrumbLeaf } from "@/src/contexts/BreadcrumbContext";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { initials } from "@/src/libs/initials";
import { AccountActions } from "@/src/pages/AccountDetail/components/AccountActions";
import { AccountConversations } from "@/src/pages/AccountDetail/components/AccountConversations";
import { AccountEventList } from "@/src/pages/AccountDetail/components/AccountEventList";
import { AccountFlags } from "@/src/pages/AccountDetail/components/AccountFlags";
import { useAccountConversations } from "@/src/pages/AccountDetail/hooks/useAccountConversations";
import { useAccountEvents } from "@/src/pages/AccountDetail/hooks/useAccountEvents";
import { useAdminAccount } from "@/src/pages/AccountDetail/hooks/useAdminAccount";
import { useFollows } from "@/src/pages/AccountDetail/hooks/useFollows";
import { ROUTES } from "@/src/routes/paths";

/**
 * Fiche d'un compte (`/accounts/:accountId`).
 *
 * Trois requêtes : `GET /moderation/accounts/{id}` pour tout ce qui décrit le
 * compte, `/profile/favorite-profiles/{id}` pour les **listes** d'abonnements,
 * et `/moderation/accounts/{id}/events` pour ses événements — que la fiche ne
 * porte qu'en compteurs dans la première réponse, exprès, pour ne pas payer
 * deux fois la même donnée à l'ouverture.
 *
 * ⚠️ **C'est le seul écran de l'admin qui affiche l'e-mail d'un tiers.** Il ne
 * sort d'aucune autre route ; ce qui se lit ici ne doit pas se retrouver dans
 * un composant partagé avec le reste de l'application.
 */
export function AccountDetail() {
  const { accountId } = useParams();
  const t = useTranslations("accounts");
  const tm = useTranslations("moderation");
  const tc = useTranslations("conversations");
  const { locale } = useLocale();

  // `Number(undefined)` vaut `NaN`, que le serveur refuserait en 400. Une URL
  // sans nombre est traitée ici comme un compte introuvable, ce qu'elle est.
  const parsed = Number(accountId);
  const isValidId = Number.isInteger(parsed) && parsed > 0;

  const { detail, isLoading, isMissing, isError } = useAdminAccount(
    isValidId ? parsed : 0,
  );

  const follows = useFollows(detail?.account.id);
  const events = useAccountEvents(detail?.account.id);
  const conversations = useAccountConversations(detail?.account.id);

  // « Comptes > nolan.dice » : l'en-tête ne peut pas résoudre l'identifiant de
  // l'URL, seul cet écran connaît le pseudo. Vide pendant le chargement,
  // effacé au départ — le hook s'en charge.
  useSetBreadcrumbLeaf(detail?.account.username);

  if (!isValidId || isMissing || (isError && !detail)) {
    return (
      <Card className="mx-auto w-full max-w-4xl p-10">
        <Card.Content>
          <EmptyState className="flex flex-col items-center gap-2 text-center">
            <p className="text-base font-medium">{t("missing-title")}</p>
            <p className="max-w-md text-pretty text-muted">
              {t("missing-description")}
            </p>
            <Link className="pt-2 text-sm" href={ROUTES.accounts}>
              {t("back-to-list")}
            </Link>
          </EmptyState>
        </Card.Content>
      </Card>
    );
  }

  if (isLoading || !detail) {
    return (
      <div aria-hidden className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        <Skeleton className="h-44 w-full rounded-lg" />
        <Skeleton className="h-28 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  const { account, profile, armies, counters, disabled, flags } = detail;

  const registered = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(account.createdAt));

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar className="size-16 shrink-0" size="lg">
            {account.avatarUrl ? (
              <Avatar.Image alt="" src={account.avatarUrl} />
            ) : null}
            <Avatar.Fallback>{initials(account.username)}</Avatar.Fallback>
          </Avatar>

          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-xl font-semibold tracking-tight">
                {account.username}
              </h2>

              {account.roles
                .filter((role) => role !== "user")
                .map((role) => (
                  <Chip color="warning" key={role} size="sm">
                    {role}
                  </Chip>
                ))}

              {account.verified ? null : (
                <Chip size="sm" variant="secondary">
                  {t("unverified")}
                </Chip>
              )}

              {/* ⚠️ La désactivation se lit **dès l'en-tête** : les actions
                  sont plus bas, et un dossier qu'on parcourt sans faire défiler
                  ne doit pas laisser croire que le compte est ouvert. */}
              {account.disabledAt ? (
                <Chip color="danger" size="sm">
                  {tm("state-disabled")}
                </Chip>
              ) : null}
            </div>

            {/* L'e-mail est ce qu'on vient chercher : il est sélectionnable
                d'un double-clic, pas noyé dans une phrase. */}
            <p className="truncate text-sm text-muted select-all">
              {account.email}
            </p>

            <p className="text-xs text-muted">
              {t("registered", { date: registered })}
              {account.locationLabel ? ` · ${account.locationLabel}` : null}
            </p>
          </div>
        </div>

        {/*
          ⚠️ **Les deux compteurs d'événements ne sont plus ici**, alors que
          `counters` les porte toujours : les sections plus bas affichent le
          même nombre, à côté de la liste qu'il compte. Deux origines pour un
          seul chiffre finissent par se contredire, et c'est alors celle sans
          liste qu'on croit à tort.

          « Signalé 7 fois » reste le chiffre qui décide sur un écran de
          modération, bien plus qu'un nombre d'abonnés.
        */}
        <ul className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <Counter label={t("followed")} value={counters.followed} />
          <Counter label={t("followers")} value={counters.followers} />
          <Counter label={t("reports-filed")} value={counters.reportsFiled} />
          <Counter
            isAlarming={counters.reportsReceived > 0}
            label={t("reports-received")}
            value={counters.reportsReceived}
          />
          <Counter
            label={tc("counter-conversations")}
            value={counters.conversations}
          />
          <Counter
            label={tc("counter-messages")}
            value={counters.messagesSent}
          />
        </ul>
      </Card>

      {/*
        Les actions **avant** le dossier, et non tout en bas : ouvrir la fiche
        d'un compte signalé, c'est venir pour décider. Les faire chercher sous
        six sections ferait défiler pour rien.
      */}
      <AccountActions account={account} disabled={disabled} />

      <DetailSection
        count={flags.length}
        emptyLabel={tm("flags-empty")}
        isEmpty={false}
        title={tm("flags")}
      >
        <AccountFlags accountId={account.id} flags={flags} />
      </DetailSection>

      <DetailSection
        emptyLabel={t("profile-none")}
        isEmpty={profile === null}
        title={t("profile")}
      >
        {profile === null ? null : (
          <div className="flex flex-col gap-3">
            {profile.bio ? (
              <p className="text-sm text-pretty whitespace-pre-wrap">
                {profile.bio}
              </p>
            ) : (
              <p className="text-sm text-muted italic">{t("no-bio")}</p>
            )}

            {(profile.tags ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(profile.tags ?? []).map((tag) => (
                  <Chip key={tag} size="sm" variant="secondary">
                    {tag}
                  </Chip>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </DetailSection>

      <DetailSection
        count={armies.length}
        emptyLabel={t("armies-empty")}
        isEmpty={armies.length === 0}
        title={t("armies")}
      >
        <ul className="flex flex-wrap gap-2">
          {armies.map((army) => (
            <li key={army.id}>
              <Chip size="sm">
                {army.army} · {army.points}
              </Chip>
            </li>
          ))}
        </ul>
      </DetailSection>

      {/*
        Les deux sens de l'abonnement, côte à côte : « qui il suit » et « qui le
        suit » se lisent ensemble quand on cherche à comprendre un réseau de
        comptes qui se signalent mutuellement.
      */}
      <div className="grid gap-4 md:grid-cols-2">
        <DetailSection
          count={follows.isLoading ? undefined : follows.followed.length}
          emptyLabel={t("followed-empty")}
          isEmpty={!follows.isLoading && follows.followed.length === 0}
          title={t("followed")}
        >
          <FollowList
            isLoading={follows.isLoading}
            profiles={follows.followed}
          />
        </DetailSection>

        <DetailSection
          count={follows.isLoading ? undefined : follows.followers.length}
          emptyLabel={t("followers-empty")}
          isEmpty={!follows.isLoading && follows.followers.length === 0}
          title={t("followers")}
        >
          <FollowList
            isLoading={follows.isLoading}
            profiles={follows.followers}
          />
        </DetailSection>
      </div>

      {/*
        ⚠️ **Les deux listes sont disjointes** : « rejoints » exclut ce que le
        compte a créé. Créer un événement y inscrit et y valide automatiquement
        l'organisateur — les additionner compterait chaque création deux fois.
      */}
      <div className="grid gap-4 md:grid-cols-2">
        <DetailSection
          count={events.isLoading ? undefined : events.totalCreated}
          emptyLabel={t("events-created-empty")}
          isEmpty={!events.isLoading && events.created.length === 0}
          title={t("events-created")}
        >
          <AccountEventList
            events={events.created}
            isLoading={events.isLoading}
            total={events.totalCreated}
          />
        </DetailSection>

        <DetailSection
          count={events.isLoading ? undefined : events.totalJoined}
          emptyLabel={t("events-joined-empty")}
          isEmpty={!events.isLoading && events.joined.length === 0}
          title={t("events-joined")}
        >
          <AccountEventList
            events={events.joined}
            isLoading={events.isLoading}
            total={events.totalJoined}
          />
        </DetailSection>
      </div>

      {/*
        ⚠️ **Le serveur journalise chaque fil ouvert depuis cette section**, avec
        l'administrateur qui l'a lu. C'est la contrepartie de l'accès : la
        modération peut lire n'importe quelle discussion d'un compte sans
        qu'un signalement l'y autorise, mais elle laisse une trace en le
        faisant. La liste, elle, ne charge aucun message.
      */}
      <DetailSection
        count={conversations.isLoading ? undefined : conversations.total}
        emptyLabel={tc("empty-list")}
        isEmpty={!conversations.isLoading && conversations.total === 0}
        title={tc("title-plural")}
      >
        <AccountConversations
          conversations={conversations.conversations}
          isLoading={conversations.isLoading}
        />
      </DetailSection>
    </div>
  );
}

function Counter({
  label,
  value,
  isAlarming = false,
}: {
  label: string;
  value: number;
  isAlarming?: boolean;
}) {
  return (
    <li className="flex flex-col items-center rounded-lg bg-default px-2 py-3 text-default-foreground">
      <span
        className={`text-lg font-semibold tabular-nums ${
          isAlarming ? "text-danger" : ""
        }`}
      >
        {value}
      </span>
      <span className="text-center text-xs text-muted">{label}</span>
    </li>
  );
}

type FollowProfile = {
  accountId: number;
  username: string;
  avatarUrl?: string | null;
};

/**
 * ⚠️ `accountId`, et non `id` : `id` est celui du **profil**, et la fiche
 * s'ouvre par compte. Les confondre mène chez quelqu'un d'autre — d'où cette
 * conversion faite à un seul endroit.
 */
function face(profile: FollowProfile) {
  return {
    avatarUrl: profile.avatarUrl,
    id: profile.accountId,
    username: profile.username,
  };
}

function FollowList({
  profiles,
  isLoading,
}: {
  profiles: FollowProfile[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div aria-hidden className="flex flex-col gap-2">
        {[0, 1, 2].map((row) => (
          <Skeleton className="h-6 w-full" key={row} />
        ))}
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {profiles.map((profile) => (
        <li key={profile.accountId}>
          <AccountBadge account={face(profile)} isLinked />
        </li>
      ))}
    </ul>
  );
}
